import { useState, useCallback } from 'react';
import { HexColorPicker, HexColorInput } from 'react-colorful';
import { Button } from '../ui/Button';
import { RotateCcw } from 'lucide-react';

const INK_COLOR = '#111827';

const DEFAULT_INVOICE_STYLE = {
  bannerColor: '#3B82F6',
  titleColor: '#10B981',
  barColor: '#3B82F6',
  barColorEnd: '#10B981',
  sectionHeaderColor: INK_COLOR,
  tenantNameColor: INK_COLOR,
};

const ELEMENT_LABELS = {
  banner: 'Portal Banner',
  tenantName: 'Company Name',
  title: 'Invoice Title',
  bar: 'Decorative Bar',
  sectionHeader: 'Section Headers',
};

/**
 * InvoiceStyleEditor - Interactive invoice style customization
 * Two-panel layout: left = clickable preview regions, right = color picker panel
 */
export default function InvoiceStyleEditor({
  invoiceStyle = {},
  primaryColor = '#3B82F6',
  secondaryColor = '#10B981',
  onSave,
  saving = false,
}) {
  const mergedStyle = {
    bannerColor: invoiceStyle.bannerColor ?? primaryColor,
    titleColor: invoiceStyle.titleColor ?? secondaryColor,
    barColor: invoiceStyle.barColor ?? primaryColor,
    barColorEnd: invoiceStyle.barColorEnd ?? secondaryColor,
    sectionHeaderColor: invoiceStyle.sectionHeaderColor ?? INK_COLOR,
    tenantNameColor: invoiceStyle.tenantNameColor ?? INK_COLOR,
  };

  const [selectedElement, setSelectedElement] = useState(null);
  const [localStyle, setLocalStyle] = useState({});

  const effectiveStyle = { ...mergedStyle, ...localStyle };

  const getDefaultForElement = useCallback(
    (element) => {
      switch (element) {
        case 'banner':
          return primaryColor;
        case 'title':
          return secondaryColor;
        case 'bar':
          return { barColor: primaryColor, barColorEnd: secondaryColor };
        case 'sectionHeader':
        case 'tenantName':
          return INK_COLOR;
        default:
          return primaryColor;
      }
    },
    [primaryColor, secondaryColor]
  );

  const handleColorChange = useCallback((key, value) => {
    setLocalStyle((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleResetElement = useCallback(() => {
    if (!selectedElement) return;
    const def = getDefaultForElement(selectedElement);
    if (typeof def === 'object') {
      setLocalStyle((prev) => ({ ...prev, ...def }));
    } else {
      const key =
        selectedElement === 'banner'
          ? 'bannerColor'
          : selectedElement === 'title'
            ? 'titleColor'
            : selectedElement === 'tenantName'
              ? 'tenantNameColor'
              : selectedElement === 'sectionHeader'
                ? 'sectionHeaderColor'
                : null;
      if (key) setLocalStyle((prev) => ({ ...prev, [key]: def }));
    }
  }, [selectedElement, getDefaultForElement]);

  const handleResetAll = useCallback(() => {
    setLocalStyle({
      bannerColor: primaryColor,
      titleColor: secondaryColor,
      barColor: primaryColor,
      barColorEnd: secondaryColor,
      sectionHeaderColor: INK_COLOR,
      tenantNameColor: INK_COLOR,
    });
  }, [primaryColor, secondaryColor]);

  const handleSave = useCallback(() => {
    onSave?.(effectiveStyle);
  }, [onSave, effectiveStyle]);

  const getColorForElement = (element) => {
    switch (element) {
      case 'banner':
        return effectiveStyle.bannerColor;
      case 'tenantName':
        return effectiveStyle.tenantNameColor;
      case 'title':
        return effectiveStyle.titleColor;
      case 'bar':
        return effectiveStyle.barColor;
      case 'sectionHeader':
        return effectiveStyle.sectionHeaderColor;
      default:
        return primaryColor;
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left: Interactive invoice preview */}
      <div className="space-y-3">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Click on an area of the invoice to customise the colours
        </p>
        <div className="border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 p-4 shadow-sm">
          <InvoicePreview
            style={effectiveStyle}
            selectedElement={selectedElement}
            onSelect={setSelectedElement}
          />
        </div>
      </div>

      {/* Right: Color picker panel */}
      <div className="space-y-4">
        {selectedElement ? (
          <>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">
                {ELEMENT_LABELS[selectedElement]}
              </h3>

              {selectedElement === 'bar' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Start colour
                    </label>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-md border border-gray-300 dark:border-gray-600 shrink-0"
                        style={{ backgroundColor: effectiveStyle.barColor }}
                      />
                      <div className="flex-1">
                        <HexColorPicker
                          color={effectiveStyle.barColor}
                          onChange={(c) => handleColorChange('barColor', c)}
                          className="!w-full !h-32"
                        />
                        <HexColorInput
                          color={effectiveStyle.barColor}
                          onChange={(c) => handleColorChange('barColor', c)}
                          prefixed
                          className="mt-2 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm font-mono"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      End colour
                    </label>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-md border border-gray-300 dark:border-gray-600 shrink-0"
                        style={{ backgroundColor: effectiveStyle.barColorEnd }}
                      />
                      <div className="flex-1">
                        <HexColorPicker
                          color={effectiveStyle.barColorEnd}
                          onChange={(c) => handleColorChange('barColorEnd', c)}
                          className="!w-full !h-32"
                        />
                        <HexColorInput
                          color={effectiveStyle.barColorEnd}
                          onChange={(c) => handleColorChange('barColorEnd', c)}
                          prefixed
                          className="mt-2 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm font-mono"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-md border border-gray-300 dark:border-gray-600 shrink-0"
                      style={{ backgroundColor: getColorForElement(selectedElement) }}
                    />
                    <div className="flex-1">
                      <HexColorPicker
                        color={getColorForElement(selectedElement)}
                        onChange={(c) => {
                          const key =
                            selectedElement === 'banner'
                              ? 'bannerColor'
                              : selectedElement === 'title'
                                ? 'titleColor'
                                : selectedElement === 'tenantName'
                                  ? 'tenantNameColor'
                                  : 'sectionHeaderColor';
                          handleColorChange(key, c);
                        }}
                        className="!w-full !h-32"
                      />
                      <HexColorInput
                        color={getColorForElement(selectedElement)}
                        onChange={(c) => {
                          const key =
                            selectedElement === 'banner'
                              ? 'bannerColor'
                              : selectedElement === 'title'
                                ? 'titleColor'
                                : selectedElement === 'tenantName'
                                  ? 'tenantNameColor'
                                  : 'sectionHeaderColor';
                          handleColorChange(key, c);
                        }}
                        prefixed
                        className="mt-2 w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm font-mono"
                      />
                    </div>
                  </div>
                </div>
              )}

              <Button
                variant="outline"
                size="sm"
                onClick={handleResetElement}
                className="mt-3"
              >
                <RotateCcw className="h-4 w-4 mr-1.5" />
                Reset to default
              </Button>
            </div>
          </>
        ) : (
          <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Select an area on the invoice preview to customise its colour
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetAll}
          >
            <RotateCcw className="h-4 w-4 mr-1.5" />
            Reset all to defaults
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Invoice Style'}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * InvoicePreview - Simplified wireframe with clickable regions
 */
function InvoicePreview({ style, selectedElement, onSelect }) {
  const regionBase =
    'cursor-pointer transition-all outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1';
  const regionUnselected =
    'border-2 border-dashed border-gray-300 dark:border-gray-500 hover:border-gray-400 dark:hover:border-gray-400';
  const regionSelected =
    'border-2 border-solid border-green-500 dark:border-green-400 ring-2 ring-green-500/20';

  const getRegionProps = (element, extraClasses = '') => ({
    className: `${regionBase} ${
      selectedElement === element ? regionSelected : regionUnselected
    } ${extraClasses}`.trim(),
    onClick: () => onSelect(element),
    role: 'button',
    tabIndex: 0,
    onKeyDown: (e) => e.key === 'Enter' && onSelect(element),
  });

  return (
    <div className="max-w-md mx-auto">
      {/* Portal banner */}
      <div
        {...getRegionProps('banner', 'h-3 rounded-t-lg')}
        style={{
          background: style.bannerColor,
        }}
        aria-label="Portal banner"
      />

      <div className="p-4 space-y-3 border-x border-b border-gray-200 dark:border-gray-600 rounded-b-lg">
        {/* Tenant name */}
        <div {...getRegionProps('tenantName', 'py-2 px-2 rounded')} aria-label="Company name">
          <div
            className="text-base font-bold"
            style={{ color: style.tenantNameColor }}
          >
            Your Company Name
          </div>
        </div>

        {/* Invoice title + bar row */}
        <div className="flex flex-col items-end gap-1">
          <div {...getRegionProps('title', 'py-1 px-2 rounded')} aria-label="Invoice title">
            <span
              className="text-lg font-extrabold tracking-wider"
              style={{ color: style.titleColor }}
            >
              INVOICE
            </span>
          </div>
          <div
            {...getRegionProps('bar', 'h-1 w-full rounded-full')}
            style={{
              background: `linear-gradient(90deg, ${style.barColor}, ${style.barColorEnd})`,
            }}
            aria-label="Decorative bar"
          />
        </div>

        {/* Section headers */}
        <div
          {...getRegionProps('sectionHeader', 'py-2 px-3 rounded-lg bg-gray-100 dark:bg-gray-700')}
          aria-label="Section headers - Materials"
        >
          <span
            className="text-xs font-bold uppercase"
            style={{ color: style.sectionHeaderColor }}
          >
            Materials
          </span>
        </div>
        <div
          {...getRegionProps('sectionHeader', 'py-2 px-3 rounded-lg bg-gray-100 dark:bg-gray-700')}
          aria-label="Section headers - Labour"
        >
          <span
            className="text-xs font-bold uppercase"
            style={{ color: style.sectionHeaderColor }}
          >
            Labour
          </span>
        </div>
      </div>
    </div>
  );
}

export { DEFAULT_INVOICE_STYLE, INK_COLOR };
